// src/econ/bridge.ts — the capex → capability bridge. This is what makes the
// path from the capex model to the scenarios module meaningful: more compute
// built (higher system capex) shifts how capable and widely adopted AI can
// become by the horizon.
//
// PURE. Input is a single scalar — the SOLVED capex ratio (system capex ÷ base,
// i.e. Result.cx) — so this file never imports or touches solve(); it cannot
// affect capex outputs (no second-order feedback, by construction). Output only
// moves slider DEFAULTS on /scenarios; the user can always override (→ USER).

import type { EconInputs, Flag } from "./types.ts";

export interface BridgeParams {
  /** Elasticity of each driver's default to the capex ratio. ESTIMATE, editable. */
  epsCapabilities: number;
  epsAdoption: number;
  epsAutonomy: number;
  epsProductivity: number;
}

/** Small, transparent elasticities. Judgement calls, not empirical — ESTIMATE. */
export const BRIDGE_PARAMS: BridgeParams = {
  epsCapabilities: 0.5,
  epsAdoption: 0.5,
  epsAutonomy: 0.2,
  epsProductivity: 0.3,
};

/** The un-bridged default scenario (capex ratio = 1 reconciles to this). The
 *  adoption default is informed by the Anthropic Economic Index (ESTIMATE). */
export const BRIDGE_DEFAULTS = {
  capabilities: 0.30,
  adoption: 0.30,
  autonomy: 0.50,
  productivity: 2.5,
} as const;

export interface BridgeResult {
  capabilities_default: number;
  adoption_default: number;
  autonomy_default: number;
  productivity_default: number;
  flag: Flag; // always ESTIMATE
  rationale: string;
}

const clamp01 = (x: number): number => (x < 0 ? 0 : x > 1 ? 1 : x);
const pow = (base: number, exp: number): number => (base <= 0 ? 0 : Math.pow(base, exp));

/**
 * Map a solved capex ratio to scenario slider defaults via a monotone power law.
 * At capexRatio = 1 the defaults equal BRIDGE_DEFAULTS exactly (reconciles-to-
 * base, like INV-1). Lower capex never yields higher defaults (monotone). The
 * result is ALWAYS flagged ESTIMATE — this link is a judgement call.
 */
export function bridge(
  capexRatio: number,
  params: BridgeParams = BRIDGE_PARAMS,
  defaults: typeof BRIDGE_DEFAULTS = BRIDGE_DEFAULTS,
): BridgeResult {
  const r = Math.max(0, capexRatio);
  return {
    capabilities_default: clamp01(defaults.capabilities * pow(r, params.epsCapabilities)),
    adoption_default: clamp01(defaults.adoption * pow(r, params.epsAdoption)),
    autonomy_default: clamp01(defaults.autonomy * pow(r, params.epsAutonomy)),
    productivity_default: Math.max(1, defaults.productivity * pow(r, params.epsProductivity)),
    flag: "ESTIMATE",
    rationale:
      "Derived from your capex scenario via an assumed elasticity. " +
      "This link is a judgement call, not an empirical estimate.",
  };
}

/** Apply a bridge result onto a base EconInputs, keeping the other fields. */
export function applyBridge(base: EconInputs, b: BridgeResult): EconInputs {
  return {
    ...base,
    capabilities: b.capabilities_default,
    adoption: b.adoption_default,
    autonomy: b.autonomy_default,
    productivity: b.productivity_default,
  };
}
